import { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';
import { useAuthStore } from '@/stores/auth';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '@/hooks/usePageTitle';
import clsx from 'clsx';
import {
    FileText,
    Plus,
    Search,
    ChevronRight,
    ChevronDown,
    MoreHorizontal,
    Pin,
    Archive,
    Trash2,
    Loader2,
    ArrowLeft,
    Bold,
    Italic,
    Heading1,
    Heading2,
    List,
    ListOrdered,
    Code,
    Quote,
    Link,
    Edit3,
    Save,
} from 'lucide-react';

interface Note {
    id: string;
    workspace_id: string;
    title: string;
    content: string;
    icon: string | null;
    cover_url: string | null;
    is_pinned: number;
    is_archived: number;
    parent_id: string | null;
    position: number;
    created_at: string;
    updated_at: string;
    author_name: string;
    child_count?: number;
}

// Common emojis for note icons
const EMOJI_OPTIONS = ['📝', '📌', '💡', '🎯', '📊', '🚀', '💼', '📚', '🔥', '⭐', '✅', '📋', '🎨', '💻', '📱', '🌟'];

export default function NotesPage() {
    const { t } = useTranslation();
    usePageTitle('notes.title');
    const { workspace } = useAuthStore();
    const queryClient = useQueryClient();

    const [selectedNoteId, setSelectedNoteId] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [expandedNotes, setExpandedNotes] = useState<Set<string>>(new Set());

    // Fetch root notes (parent_id = null)
    const { data: notesData, isLoading } = useQuery({
        queryKey: ['notes', workspace?.id, searchQuery],
        queryFn: () => api.getNotes(workspace!.id, {
            parentId: searchQuery ? undefined : null,
            search: searchQuery || undefined
        }),
        enabled: !!workspace?.id,
    });

    // Fetch selected note details
    const { data: selectedNoteData } = useQuery({
        queryKey: ['note', selectedNoteId],
        queryFn: () => api.getNote(selectedNoteId!),
        enabled: !!selectedNoteId,
    });

    // Create note mutation
    const createNoteMutation = useMutation({
        mutationFn: (data: { title?: string; parent_id?: string | null }) =>
            api.createNote({
                workspace_id: workspace!.id,
                title: data.title || 'Untitled',
                content: '',
                parent_id: data.parent_id,
            }),
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['notes'] });
            setSelectedNoteId(data.data.id);
        },
    });

    const handleCreateNote = (parentId?: string | null) => {
        createNoteMutation.mutate({ parent_id: parentId });
    };

    const toggleExpand = (noteId: string) => {
        const next = new Set(expandedNotes);
        if (next.has(noteId)) {
            next.delete(noteId);
        } else {
            next.add(noteId);
        }
        setExpandedNotes(next);
    };

    const notes = notesData?.data || [];
    const selectedNote = selectedNoteData?.data;

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
            </div>
        );
    }

    return (
        <div className="flex h-[calc(100vh-8rem)] -m-4 lg:-m-6">
            {/* Sidebar - Notes List */}
            <div className="w-72 border-r border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800/50 flex flex-col">
                {/* Sidebar Header */}
                <div className="p-4 border-b border-neutral-200 dark:border-neutral-700">
                    <div className="flex items-center justify-between mb-3">
                        <h2 className="font-semibold flex items-center gap-2">
                            <FileText className="w-5 h-5 text-primary-600" />
                            {t('notes.title', 'Notes')}
                        </h2>
                        <button
                            onClick={() => handleCreateNote()}
                            disabled={createNoteMutation.isPending}
                            className="p-1.5 hover:bg-neutral-200 dark:hover:bg-neutral-700 rounded-lg transition-colors"
                            title={t('notes.create', 'New note')}
                        >
                            {createNoteMutation.isPending ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <Plus className="w-4 h-4" />
                            )}
                        </button>
                    </div>

                    {/* Search */}
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder={t('notes.search', 'Search notes...')}
                            className="w-full pl-9 pr-3 py-2 text-sm bg-white dark:bg-neutral-700 border border-neutral-200 dark:border-neutral-600 rounded-lg"
                        />
                    </div>
                </div>

                {/* Notes Tree */}
                <div className="flex-1 overflow-y-auto p-2">
                    {notes.length === 0 ? (
                        <div className="text-center py-8 text-neutral-500">
                            <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
                            <p className="text-sm">{t('notes.empty', 'No notes yet')}</p>
                            <button
                                onClick={() => handleCreateNote()}
                                className="mt-3 text-sm text-primary-600 hover:text-primary-700"
                            >
                                {t('notes.createFirst', 'Create your first note')}
                            </button>
                        </div>
                    ) : (
                        <div className="space-y-0.5">
                            {notes.map((note) => (
                                <NoteTreeItem
                                    key={note.id}
                                    note={note}
                                    isSelected={selectedNoteId === note.id}
                                    isExpanded={expandedNotes.has(note.id)}
                                    onSelect={() => setSelectedNoteId(note.id)}
                                    onToggleExpand={() => toggleExpand(note.id)}
                                    onCreateChild={() => handleCreateNote(note.id)}
                                    depth={0}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Main Content - Note Editor */}
            <div className="flex-1 flex flex-col bg-white dark:bg-neutral-900">
                {selectedNote ? (
                    <NoteEditor
                        note={selectedNote}
                        onBack={() => setSelectedNoteId(null)}
                    />
                ) : (
                    <div className="flex-1 flex items-center justify-center text-neutral-400">
                        <div className="text-center">
                            <FileText className="w-16 h-16 mx-auto mb-4 opacity-30" />
                            <p>{t('notes.selectOrCreate', 'Select a note or create a new one')}</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function NoteTreeItem({
    note,
    isSelected,
    isExpanded,
    onSelect,
    onToggleExpand,
    onCreateChild,
    depth,
}: {
    note: Note;
    isSelected: boolean;
    isExpanded: boolean;
    onSelect: () => void;
    onToggleExpand: () => void;
    onCreateChild: () => void;
    depth: number;
}) {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const [showMenu, setShowMenu] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);

    const { data: childrenData } = useQuery({
        queryKey: ['notes-children', note.id],
        queryFn: () => api.getNotes(note.workspace_id, { parentId: note.id }),
        enabled: isExpanded && (note.child_count || 0) > 0,
    });

    const pinMutation = useMutation({
        mutationFn: () => api.toggleNotePin(note.id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['notes'] });
        },
    });

    const archiveMutation = useMutation({
        mutationFn: () => api.toggleNoteArchive(note.id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['notes'] });
        },
    });

    const deleteMutation = useMutation({
        mutationFn: () => api.deleteNote(note.id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['notes'] });
        },
    });

    useEffect(() => {
        function handleClickOutside(e: MouseEvent) {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
                setShowMenu(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const hasChildren = (note.child_count || 0) > 0;
    const children = childrenData?.data || [];

    return (
        <div>
            <div
                className={clsx(
                    'group flex items-center gap-1 px-2 py-1.5 rounded-lg cursor-pointer transition-colors',
                    isSelected
                        ? 'bg-primary-100 dark:bg-primary-600/20 text-primary-700 dark:text-primary-300'
                        : 'hover:bg-neutral-200 dark:hover:bg-neutral-700'
                )}
                style={{ paddingLeft: `${depth * 16 + 8}px` }}
            >
                {/* Expand/Collapse */}
                <button
                    onClick={(e) => { e.stopPropagation(); onToggleExpand(); }}
                    className={clsx(
                        'p-0.5 rounded hover:bg-neutral-300 dark:hover:bg-neutral-600',
                        !hasChildren && 'invisible'
                    )}
                >
                    {isExpanded ? (
                        <ChevronDown className="w-3.5 h-3.5" />
                    ) : (
                        <ChevronRight className="w-3.5 h-3.5" />
                    )}
                </button>

                {/* Icon */}
                <span className="text-base flex-shrink-0">
                    {note.icon || '📄'}
                </span>

                {/* Title */}
                <span
                    className="flex-1 text-sm truncate"
                    onClick={onSelect}
                >
                    {note.title || 'Untitled'}
                </span>

                {/* Pin indicator */}
                {note.is_pinned === 1 && (
                    <Pin className="w-3 h-3 text-amber-500 flex-shrink-0" />
                )}

                {/* Actions Menu */}
                <div className="relative" ref={menuRef}>
                    <button
                        onClick={(e) => { e.stopPropagation(); setShowMenu(!showMenu); }}
                        className="p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-neutral-300 dark:hover:bg-neutral-600"
                    >
                        <MoreHorizontal className="w-3.5 h-3.5" />
                    </button>

                    {showMenu && (
                        <div className="absolute right-0 top-full mt-1 w-40 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg shadow-lg py-1 z-50">
                            <button
                                onClick={() => { onCreateChild(); setShowMenu(false); }}
                                className="w-full px-3 py-1.5 text-left text-sm hover:bg-neutral-100 dark:hover:bg-neutral-700 flex items-center gap-2"
                            >
                                <Plus className="w-4 h-4" />
                                {t('notes.addSubpage', 'Add subpage')}
                            </button>
                            <button
                                onClick={() => { pinMutation.mutate(); setShowMenu(false); }}
                                className="w-full px-3 py-1.5 text-left text-sm hover:bg-neutral-100 dark:hover:bg-neutral-700 flex items-center gap-2"
                            >
                                <Pin className="w-4 h-4" />
                                {note.is_pinned ? t('notes.unpin', 'Unpin') : t('notes.pin', 'Pin')}
                            </button>
                            <button
                                onClick={() => { archiveMutation.mutate(); setShowMenu(false); }}
                                className="w-full px-3 py-1.5 text-left text-sm hover:bg-neutral-100 dark:hover:bg-neutral-700 flex items-center gap-2"
                            >
                                <Archive className="w-4 h-4" />
                                {note.is_archived ? t('notes.unarchive', 'Unarchive') : t('notes.archive', 'Archive')}
                            </button>
                            <hr className="my-1 border-neutral-200 dark:border-neutral-700" />
                            <button
                                onClick={() => {
                                    if (confirm(t('notes.deleteConfirm', 'Delete this note?'))) {
                                        deleteMutation.mutate();
                                    }
                                    setShowMenu(false);
                                }}
                                className="w-full px-3 py-1.5 text-left text-sm text-danger-600 hover:bg-danger-50 dark:hover:bg-danger-600/20 flex items-center gap-2"
                            >
                                <Trash2 className="w-4 h-4" />
                                {t('common.delete', 'Delete')}
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Children */}
            {isExpanded && hasChildren && (
                <div>
                    {children.map((child) => (
                        <NoteTreeItem
                            key={child.id}
                            note={child}
                            isSelected={false}
                            isExpanded={false}
                            onSelect={() => { }}
                            onToggleExpand={() => { }}
                            onCreateChild={() => { }}
                            depth={depth + 1}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

function NoteEditor({ note, onBack }: { note: any; onBack: () => void }) {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const [title, setTitle] = useState(note.title || '');
    const [content, setContent] = useState(note.content || '');
    const [icon, setIcon] = useState(note.icon || '📄');
    const [showIconPicker, setShowIconPicker] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const contentRef = useRef<HTMLTextAreaElement>(null);

    // Update local state when note changes
    useEffect(() => {
        setTitle(note.title || '');
        setContent(note.content || '');
        setIcon(note.icon || '📄');
        setIsEditing(false);
        setHasChanges(false);
    }, [note.id]);

    const updateNoteMutation = useMutation({
        mutationFn: (data: { title?: string; content?: string; icon?: string }) =>
            api.updateNote(note.id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['notes'] });
            queryClient.invalidateQueries({ queryKey: ['note', note.id] });
            setHasChanges(false);
        },
    });

    // Manual save
    const handleSave = useCallback(() => {
        if (!hasChanges) return;
        setIsSaving(true);
        updateNoteMutation.mutate(
            { title, content, icon },
            {
                onSettled: () => setIsSaving(false),
            }
        );
    }, [title, content, icon, hasChanges, updateNoteMutation]);

    const handleTitleChange = (newTitle: string) => {
        setTitle(newTitle);
        setHasChanges(true);
    };

    const handleContentChange = (newContent: string) => {
        setContent(newContent);
        setHasChanges(true);
    };

    const handleIconChange = (newIcon: string) => {
        setIcon(newIcon);
        setShowIconPicker(false);
        setHasChanges(true);
    };

    const handleEdit = () => {
        setIsEditing(true);
    };

    const handleCancelEdit = () => {
        // Reset to original values
        setTitle(note.title || '');
        setContent(note.content || '');
        setIcon(note.icon || '📄');
        setIsEditing(false);
        setHasChanges(false);
    };

    // Toolbar actions for formatting
    const insertMarkdown = (prefix: string, suffix: string = '') => {
        const textarea = contentRef.current;
        if (!textarea) return;

        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const selected = content.substring(start, end);
        const newContent = content.substring(0, start) + prefix + selected + suffix + content.substring(end);

        setContent(newContent);
        setHasChanges(true);

        // Restore cursor position
        setTimeout(() => {
            textarea.focus();
            const newPos = start + prefix.length + selected.length + suffix.length;
            textarea.setSelectionRange(newPos, newPos);
        }, 0);
    };

    // Convert markdown to HTML for preview
    const renderMarkdown = (text: string) => {
        if (!text) return <p className="text-neutral-400 italic">{t('notes.noContent', 'No content yet')}</p>;

        // Simple markdown renderer
        const lines = text.split('\n');
        return lines.map((line, i) => {
            // Headers
            if (line.startsWith('### ')) {
                return <h3 key={i} className="text-lg font-semibold mt-4 mb-2">{line.slice(4)}</h3>;
            }
            if (line.startsWith('## ')) {
                return <h2 key={i} className="text-xl font-semibold mt-5 mb-2">{line.slice(3)}</h2>;
            }
            if (line.startsWith('# ')) {
                return <h1 key={i} className="text-2xl font-bold mt-6 mb-3">{line.slice(2)}</h1>;
            }
            // Quote
            if (line.startsWith('> ')) {
                return <blockquote key={i} className="border-l-4 border-neutral-300 dark:border-neutral-600 pl-4 italic text-neutral-600 dark:text-neutral-400 my-2">{line.slice(2)}</blockquote>;
            }
            // List items
            if (line.match(/^[-*] /)) {
                return <li key={i} className="ml-4 list-disc">{formatInlineMarkdown(line.slice(2))}</li>;
            }
            if (line.match(/^\d+\. /)) {
                return <li key={i} className="ml-4 list-decimal">{formatInlineMarkdown(line.replace(/^\d+\. /, ''))}</li>;
            }
            // Empty line
            if (line.trim() === '') {
                return <br key={i} />;
            }
            // Regular paragraph
            return <p key={i} className="my-1">{formatInlineMarkdown(line)}</p>;
        });
    };

    // Format inline markdown (bold, italic, code, links)
    const formatInlineMarkdown = (text: string) => {
        // Code blocks
        const parts: React.ReactNode[] = [];
        let remaining = text;
        let key = 0;

        // Handle inline code
        while (remaining.includes('`')) {
            const startIdx = remaining.indexOf('`');
            const endIdx = remaining.indexOf('`', startIdx + 1);
            if (endIdx === -1) break;

            if (startIdx > 0) {
                parts.push(<span key={key++}>{formatBoldItalic(remaining.slice(0, startIdx))}</span>);
            }
            parts.push(
                <code key={key++} className="px-1.5 py-0.5 bg-neutral-100 dark:bg-neutral-700 rounded text-sm font-mono">
                    {remaining.slice(startIdx + 1, endIdx)}
                </code>
            );
            remaining = remaining.slice(endIdx + 1);
        }

        if (remaining) {
            parts.push(<span key={key++}>{formatBoldItalic(remaining)}</span>);
        }

        return parts.length > 0 ? parts : text;
    };

    // Format bold and italic
    const formatBoldItalic = (text: string): React.ReactNode => {
        // Bold **text**
        text = text.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
        // Italic *text*
        text = text.replace(/\*(.+?)\*/g, '<em>$1</em>');
        // Links [text](url)
        text = text.replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" class="text-primary-600 hover:underline">$1</a>');

        return <span dangerouslySetInnerHTML={{ __html: text }} />;
    };

    return (
        <div className="flex-1 flex flex-col overflow-hidden">
            {/* Header */}
            <div className="flex items-center gap-3 px-6 py-4 border-b border-neutral-200 dark:border-neutral-700">
                <button
                    onClick={onBack}
                    className="p-1.5 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg lg:hidden"
                >
                    <ArrowLeft className="w-5 h-5" />
                </button>

                {/* Breadcrumb */}
                {note.breadcrumb && note.breadcrumb.length > 0 && (
                    <div className="flex items-center gap-1 text-sm text-neutral-500">
                        {note.breadcrumb.map((b: { id: string; title: string }, i: number) => (
                            <span key={b.id} className="flex items-center gap-1">
                                <span className="hover:underline cursor-pointer">{b.title}</span>
                                {i < note.breadcrumb.length - 1 && <ChevronRight className="w-3 h-3" />}
                            </span>
                        ))}
                        <ChevronRight className="w-3 h-3" />
                    </div>
                )}

                <div className="flex-1" />

                {/* Action buttons */}
                <div className="flex items-center gap-2">
                    {isEditing ? (
                        <>
                            {/* Cancel button */}
                            <button
                                onClick={handleCancelEdit}
                                className="px-3 py-1.5 text-sm text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg transition-colors"
                            >
                                {t('common.cancel', 'Cancel')}
                            </button>
                            {/* Save button */}
                            <button
                                onClick={handleSave}
                                disabled={!hasChanges || isSaving}
                                className={clsx(
                                    'flex items-center gap-2 px-4 py-1.5 rounded-lg text-sm font-medium transition-colors',
                                    hasChanges
                                        ? 'bg-primary-600 text-white hover:bg-primary-700'
                                        : 'bg-neutral-200 dark:bg-neutral-700 text-neutral-500 cursor-not-allowed'
                                )}
                            >
                                {isSaving ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <Save className="w-4 h-4" />
                                )}
                                {t('common.save', 'Save')}
                            </button>
                        </>
                    ) : (
                        <button
                            onClick={handleEdit}
                            className="flex items-center gap-2 px-4 py-1.5 bg-primary-600 text-white hover:bg-primary-700 rounded-lg text-sm font-medium transition-colors"
                        >
                            <Edit3 className="w-4 h-4" />
                            {t('common.edit', 'Edit')}
                        </button>
                    )}
                </div>
            </div>

            {/* Editor Content */}
            <div className="flex-1 overflow-y-auto">
                <div className="max-w-3xl mx-auto px-8 py-8">
                    {/* Icon + Title */}
                    <div className="flex items-start gap-3 mb-6">
                        <div className="relative">
                            {isEditing ? (
                                <button
                                    onClick={() => setShowIconPicker(!showIconPicker)}
                                    className="text-4xl hover:bg-neutral-100 dark:hover:bg-neutral-700 p-2 rounded-lg transition-colors"
                                >
                                    {icon}
                                </button>
                            ) : (
                                <span className="text-4xl p-2">{icon}</span>
                            )}

                            {showIconPicker && isEditing && (
                                <div className="absolute top-full left-0 mt-2 p-3 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-xl shadow-xl z-50">
                                    <div className="grid grid-cols-8 gap-1">
                                        {EMOJI_OPTIONS.map((emoji) => (
                                            <button
                                                key={emoji}
                                                onClick={() => handleIconChange(emoji)}
                                                className="w-8 h-8 text-xl hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded flex items-center justify-center"
                                            >
                                                {emoji}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {isEditing ? (
                            <input
                                type="text"
                                value={title}
                                onChange={(e) => handleTitleChange(e.target.value)}
                                placeholder={t('notes.untitled', 'Untitled')}
                                className="flex-1 text-3xl font-bold bg-transparent border-none outline-none placeholder:text-neutral-400"
                            />
                        ) : (
                            <h1 className="flex-1 text-3xl font-bold">{title || t('notes.untitled', 'Untitled')}</h1>
                        )}
                    </div>

                    {isEditing ? (
                        <>
                            {/* Toolbar */}
                            <div className="flex items-center gap-1 mb-4 p-2 bg-neutral-50 dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700">
                                <ToolbarButton icon={Bold} title="Bold (Ctrl+B)" onClick={() => insertMarkdown('**', '**')} />
                                <ToolbarButton icon={Italic} title="Italic (Ctrl+I)" onClick={() => insertMarkdown('*', '*')} />
                                <div className="w-px h-5 bg-neutral-300 dark:bg-neutral-600 mx-1" />
                                <ToolbarButton icon={Heading1} title="Heading 1" onClick={() => insertMarkdown('# ', '')} />
                                <ToolbarButton icon={Heading2} title="Heading 2" onClick={() => insertMarkdown('## ', '')} />
                                <div className="w-px h-5 bg-neutral-300 dark:bg-neutral-600 mx-1" />
                                <ToolbarButton icon={List} title="Bullet List" onClick={() => insertMarkdown('- ', '')} />
                                <ToolbarButton icon={ListOrdered} title="Numbered List" onClick={() => insertMarkdown('1. ', '')} />
                                <div className="w-px h-5 bg-neutral-300 dark:bg-neutral-600 mx-1" />
                                <ToolbarButton icon={Code} title="Code" onClick={() => insertMarkdown('`', '`')} />
                                <ToolbarButton icon={Quote} title="Quote" onClick={() => insertMarkdown('> ', '')} />
                                <ToolbarButton icon={Link} title="Link" onClick={() => insertMarkdown('[', '](url)')} />
                            </div>

                            {/* Content Textarea */}
                            <textarea
                                ref={contentRef}
                                value={content}
                                onChange={(e) => handleContentChange(e.target.value)}
                                placeholder={t('notes.placeholder', 'Start writing...')}
                                className="w-full min-h-[400px] text-base leading-relaxed bg-transparent border-none outline-none resize-none placeholder:text-neutral-400 font-mono"
                                spellCheck={false}
                            />
                        </>
                    ) : (
                        /* View mode - Markdown Preview */
                        <div className="prose dark:prose-invert max-w-none min-h-[400px]">
                            {renderMarkdown(content)}
                        </div>
                    )}

                    {/* Subpages */}
                    {note.children && note.children.length > 0 && (
                        <div className="mt-8 pt-6 border-t border-neutral-200 dark:border-neutral-700">
                            <h3 className="text-sm font-semibold text-neutral-500 mb-3">
                                {t('notes.subpages', 'Subpages')}
                            </h3>
                            <div className="grid grid-cols-2 gap-2">
                                {note.children.map((child: any) => (
                                    <div
                                        key={child.id}
                                        className="flex items-center gap-2 p-3 bg-neutral-50 dark:bg-neutral-800 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-700 cursor-pointer"
                                    >
                                        <span className="text-xl">{child.icon || '📄'}</span>
                                        <span className="text-sm truncate">{child.title || 'Untitled'}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function ToolbarButton({
    icon: Icon,
    title,
    onClick
}: {
    icon: any;
    title: string;
    onClick: () => void;
}) {
    return (
        <button
            onClick={onClick}
            title={title}
            className="p-2 hover:bg-neutral-200 dark:hover:bg-neutral-700 rounded transition-colors"
        >
            <Icon className="w-4 h-4" />
        </button>
    );
}
